import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    Edit,
    NumberInput,
    TextInput,
    TabbedForm,
    FormTab,
    SaveButton,
    Toolbar,
    DateInput,
    TextField,
    Labeled,
    FunctionField,
    ReferenceField,
    DateField,
    ListButton,
    TopToolbar,
    useRecordContext,
    useEditContext,
    useNotify,
    useTranslate,
} from 'react-admin';
import {
    required,
    minValue,
    maxValue,
    minLength,
    maxLength,
    number,
    email,
    regex,
    choices,
    useRefresh,
} from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';
import {
    Grid,
    Box,
    Typography,
    Stack,
    Paper,
    CircularProgress,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import BusinessIcon from '@mui/icons-material/Business';
import StatisticsTile from '../../components/StatisticsTile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import {
    CustomRadioButtonGroupInput,
    CustomBulkDeleteButton,
    CustomUserLicenseList,
    CustomLogEventList,
    CustomCheckboxGroupInput,
    CustomSubscriptionList,
    CustomUserList,
    CustomProductList,
    CustomAddSubscriptionButton,
    CustomGroupList,
    CustomSelectArrayInput,
} from '../controls';

import user from '../user';

import {
    LibraryBooks,
    Article,
    Podcasts,
    Attachment,
    DataObject,
    History,
    Person,
    ReceiptLong,
    TransferWithinAStation,
    Diversity1,
    Subscriptions,
    School,
    Face,
    Face5,
} from '@mui/icons-material';
import CreateUserDialogButton from '../user/CreateUserDialogButton';
import { Alert } from '@mui/material';
import { getSchoolTypeDescription } from './schoolTypes';
import SchoolMergeTab from './SchoolMergeTab';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import { useLocation } from 'react-router-dom';

const SchoolTypeInput = () => {
    const record = useRecordContext();
    const type = String(record?.public?.type ?? '');

    return (
        <TextInput
            resource="school"
            source="public.type"
            helperText={type ? getSchoolTypeDescription(type) : ''}
            fullWidth
            readOnly
        />
    );
};

const EditToolbar = () => {
    const { pathname } = useLocation();
    if (pathname.endsWith('/yhdistaminen')) return null;
    return (
        <Toolbar>
            <SaveButton />
        </Toolbar>
    );
};

const SchoolEditActions = () => {
    /*
    const school = useEditContext();
    console.log('school', school);
    */
    return (
        <TopToolbar>
            <CreateUserDialogButton resource="teacher" />
            {/*<CreateUserDialogButton resource="student" />*/}
            <CreateUserDialogButton resource="pupil" />
        </TopToolbar>
    );
};

// Component to display organization data from Opintopolku API
const OrganizationInfoTab = ({ onStatusChange }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const [organizationData, setOrganizationData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (record?.id) {
            setLoading(true);
            setError(null);

            // Use the backend proxy endpoint instead of calling Opintopolku directly
            fetch(`/api/school/${record.id}/organization`, {
                headers: {
                    Authorization: localStorage.getItem('token')
                        ? `Bearer ${localStorage.getItem('token')}`
                        : '',
                    'Content-Type': 'application/json',
                },
            })
                .then(response => {
                    if (!response.ok) {
                        if (response.status === 404) {
                            onStatusChange?.(false); // Notify parent about inactive status
                            throw new Error(
                                translate(
                                    'resources.school.organization.not_found'
                                )
                            );
                        }
                        throw new Error(
                            `HTTP error! status: ${response.status}`
                        );
                    }
                    return response.json();
                })
                .then(data => {
                    setOrganizationData(data);
                    setLoading(false);
                    // Check if organization status is active
                    const isActive = data?.status === 'AKTIIVINEN';
                    onStatusChange?.(isActive);
                })
                .catch(err => {
                    setError(err.message);
                    setLoading(false);
                    onStatusChange?.(false); // Notify parent about error/inactive status
                });
        }
    }, [record?.id, onStatusChange, translate]);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
            >
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Ladataan organisaatiotietoja...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Paper
                sx={{
                    p: 2,
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                }}
            >
                <Typography variant="h6">Virhe ladattaessa tietoja</Typography>
                <Typography variant="body2">{error}</Typography>
            </Paper>
        );
    }

    if (!record?.id) {
        return (
            <Paper sx={{ p: 2, bgcolor: 'warning.light' }}>
                <Typography variant="body1">
                    Koulun tietoja ei löytynyt. Organisaatiotietoja ei voida
                    hakea.
                </Typography>
            </Paper>
        );
    }

    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <Typography variant="h5" paragraph>
                    Organisaatiotiedot Opintopolusta
                </Typography>
                {organizationData ? (
                    <>
                        {organizationData.status && (
                            <Paper
                                sx={{
                                    p: 1,
                                    mb: 2,
                                    bgcolor:
                                        organizationData.status === 'AKTIIVINEN'
                                            ? 'success.light'
                                            : 'warning.light',
                                    color:
                                        organizationData.status === 'AKTIIVINEN'
                                            ? 'success.contrastText'
                                            : 'warning.contrastText',
                                }}
                            >
                                <Typography variant="body2">
                                    <strong>Status:</strong>{' '}
                                    {organizationData.status}
                                    {organizationData.status !==
                                        'AKTIIVINEN' && (
                                        <span>
                                            {' '}
                                            -{' '}
                                            {translate(
                                                'resources.school.organization.inactive'
                                            )}
                                        </span>
                                    )}
                                </Typography>
                            </Paper>
                        )}
                        <Paper sx={{ p: 2 }}>
                            <pre
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '0.875rem',
                                    fontFamily: 'monospace',
                                    maxHeight: '600px',
                                    overflow: 'auto',
                                }}
                            >
                                {JSON.stringify(organizationData, null, 2)}
                            </pre>
                        </Paper>
                    </>
                ) : (
                    <Typography variant="body1">
                        Ei organisaatiotietoja saatavilla koululle: {record.id}
                    </Typography>
                )}
            </Grid>
        </Grid>
    );
};

const SchoolEdit = () => {
    const refresh = useRefresh(); // Uncommented to enable refreshing after actions
    const notify = useNotify();
    const translate = useTranslate();
    const {
        canAccess: canManageMerges,
        isPending: isMergeAccessPending,
    } = useCanAccess({
        action: 'read',
        resource: 'merge.workspace',
    });
    const [organizationActive, setOrganizationActive] = useState(true); // Track organization status

    const copyUserAndPass = r => {
        //e.stopPropagation();
        //console.log(r);
        navigator.clipboard.writeText(
            'Käyttäjätunnus: ' +
                r.public.identifier +
                '\nSalasana: ' +
                r.public.shop_password
        );

        notify(
            <Alert severity="info">
                Käyttäjätunnus ja salasana on kopioitu leikepöydälle.
            </Alert>
        );
    };

    return (
        <Edit
            redirect={false}
            mutationMode="optimistic"
            actions={<SchoolEditActions />}
        >
            <TabbedForm toolbar={<EditToolbar />}>
                {/* Tähän perustiedot */}
                <FormTab
                    label="resources.school.tabs.details"
                    icon={<Article />}
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <FunctionField
                                render={record => (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{ mb: 2 }}
                                    >
                                        {record?.private?.services
                                            ?.opepalvelu && (
                                            <span
                                                style={{
                                                    backgroundColor: '#afbec5',
                                                    fontSize: 12,
                                                    color: 'rgb(255, 255, 255)',
                                                    padding: '4px 8px',
                                                    borderRadius: 3,
                                                    background:
                                                        'rgba(96, 125, 139, 0.5)',
                                                    height: 24,
                                                }}
                                            >
                                                OOP
                                            </span>
                                        )}
                                        {record?.private?.services
                                            ?.finnlectura && (
                                            <span
                                                style={{
                                                    backgroundColor: '#afbec5',
                                                    fontSize: 12,
                                                    color: 'rgb(255, 255, 255)',
                                                    padding: '4px 8px',
                                                    borderRadius: 3,
                                                    background:
                                                        'rgba(96, 125, 139, 0.5)',
                                                    height: 24,
                                                }}
                                            >
                                                FL
                                            </span>
                                        )}
                                        {record?.private?.services?.crm && (
                                            <a
                                                href={`${process.env.REACT_APP_CRM_URL}/main.aspx?appid=b68a0d15-8769-4bc0-8999-f949f7404bbb&pagetype=entityrecord&etn=account&id=${record.private.services.crm.crmGUID}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    backgroundColor: '#afbec5',
                                                    fontSize: 12,
                                                    color: 'rgb(255, 255, 255)',
                                                    padding: '4px 8px',
                                                    borderRadius: 3,
                                                    background:
                                                        'rgba(96, 125, 139, 0.5)',
                                                    height: 24,
                                                    textDecoration: 'none',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={e =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                CRM
                                            </a>
                                        )}
                                    </Stack>
                                )}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextInput
                                resource="school"
                                source="name"
                                validate={[required()]}
                                fullWidth
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display={{ xs: 'block', sm: 'flex' }}>
                                <Box flex={1} mr={{ xs: '0.5em' }}>
                                    <CustomCheckboxGroupInput
                                        resource="school"
                                        source="public.categories"
                                        url="extralist/product/categorygroups"
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display={{ xs: 'block', sm: 'flex' }}>
                                <Box flex={1} mr={{ xs: '0.5em' }}>
                                    <CustomCheckboxGroupInput
                                        resource="school"
                                        source="public.hidepages"
                                        url="extralist/school/hidepages"
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display={{ xs: 'block', sm: 'flex' }}>
                                <Box flex={1} mr={{ xs: '0.5em' }}>
                                    <CustomCheckboxGroupInput
                                        resource="school"
                                        source="public.features"
                                        url="extralist/school/features"
                                    />

                                    <CustomSelectArrayInput
                                        resource="school"
                                        source="public.envs"
                                        url="extralist/school/envs"
                                        label="Ympäristöt"
                                    />

                                    <TextInput
                                        resource="school"
                                        source="public.school_email"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />

                                    <TextInput
                                        resource="school"
                                        source="private.aspa_info"
                                        multiline
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        {/* <Grid size={12}> */}

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display={{ xs: 'block', sm: 'flex' }}>
                                <Box flex={1} mr={{ xs: '0.5em' }}>
                                    <TextInput
                                        resource="school"
                                        source="public.headmaster"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.headmaster_phone"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.headmaster_cellphone"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.headmaster_email"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display={{ xs: 'block', sm: 'flex' }}>
                                <Box flex={1} mr={{ xs: '0.5em' }}>
                                    <TextInput
                                        resource="school"
                                        source="public.number_of_teachers"
                                        validate={[maxLength(3)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.number_of_pupils"
                                        validate={[maxLength(4)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.grade_min"
                                        validate={[maxLength(4)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.grade_max"
                                        validate={[maxLength(4)]}
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display={{ xs: 'block', sm: 'flex' }}>
                                <Box flex={1} mr={{ xs: '0.5em' }}>
                                    <TextInput
                                        resource="school"
                                        source="public.phone_1"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.phone_2"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.office_phone"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display={{ xs: 'block', sm: 'flex' }}>
                                <Box flex={1} mr={{ xs: '0.5em' }}>
                                    <TextInput
                                        resource="school"
                                        source="public.address"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.zip_code"
                                        validate={[maxLength(15)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.city"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display={{ xs: 'block', sm: 'flex' }}>
                                <Box flex={1} mr={{ xs: '0.5em' }}>
                                    <TextInput
                                        resource="school"
                                        source="public.visit_address"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.visit_zip_code"
                                        validate={[maxLength(15)]}
                                        fullWidth
                                    />
                                    <TextInput
                                        resource="school"
                                        source="public.visit_city"
                                        validate={[maxLength(50)]}
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display={{ xs: 'block', sm: 'flex' }}>
                                <Box flex={1} mr={{ xs: '0.5em' }}>
                                    <FunctionField
                                        render={record => (
                                            <ContentCopyIcon
                                                onClick={() =>
                                                    copyUserAndPass(record)
                                                }
                                            />
                                        )}
                                    />
                                </Box>
                                <Box flex={14} mr={{ xs: '0.5em' }}>
                                    <TextInput
                                        resource="school"
                                        source="public.identifier"
                                        fullWidth
                                        readOnly
                                    />

                                    <TextInput
                                        resource="school"
                                        source="public.shop_password"
                                        fullWidth
                                        readOnly
                                    />

                                    <TextInput
                                        resource="school"
                                        source="public.code"
                                        fullWidth
                                        readOnly
                                    />
                                    <SchoolTypeInput />
                                </Box>
                            </Box>
                        </Grid>

                        {/* </Grid> */}

                        <Grid size={12}>
                            <Stack direction="row" spacing={4}>
                                <Box>
                                    <Labeled label="resources.pupil.fields.author.creator">
                                        <>
                                            <ReferenceField
                                                reference="user"
                                                source="author.creator"
                                            >
                                                <FunctionField
                                                    render={record =>
                                                        `${record.public.firstname} ${record.public.lastname}`
                                                    }
                                                />
                                            </ReferenceField>
                                            <DateField
                                                resource="school"
                                                source="author.created"
                                                showTime
                                                locales={'fi-FI'}
                                            />
                                        </>
                                    </Labeled>
                                </Box>
                                <Box>
                                    <Labeled label="resources.pupil.fields.author.modifier">
                                        <>
                                            <ReferenceField
                                                reference="user"
                                                source="author.modifier"
                                            >
                                                <FunctionField
                                                    render={record =>
                                                        `${record.public.firstname} ${record.public.lastname}`
                                                    }
                                                />
                                            </ReferenceField>
                                            <DateField
                                                resource="school"
                                                source="author.modified"
                                                showTime
                                                locales={'fi-FI'}
                                            />
                                        </>
                                    </Labeled>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </FormTab>
                {/* 
                    - Tähän tilauksiin tiedot. 
                    - Lista oppilaslisensseistä
                    - Lista koululisensseistä
                    - Mahdollisuus lisätä lisenssi
                */}
                {
                    <FormTab
                        label="resources.school.tabs.subscription"
                        icon={<Subscriptions />}
                        path="tilaukset"
                    >
                        <Grid container width={{ xs: '100%' }} spacing={2}>
                            <Grid size={12}>
                                <Typography variant="h5" paragraph>
                                    Lisää tilaus
                                </Typography>
                                <CustomProductList
                                    resource="product"
                                    disableSyncWithLocation
                                    storeKey={false}
                                    customFilters={[
                                        'use_query_min_length',
                                        'use_v2',
                                    ]}
                                    showExtra={['search', 'info']}
                                    sort={{ field: 'name', order: 'ASC' }}
                                    bulkActionButtons={
                                        <CustomAddSubscriptionButton />
                                    }
                                >
                                    <span />
                                </CustomProductList>
                            </Grid>
                        </Grid>

                        <Grid container width={{ xs: '100%' }} spacing={2}>
                            <Grid size={12}>
                                <Typography variant="h5" paragraph>
                                    Oppilaslisenssit
                                </Typography>
                                <CustomSubscriptionList
                                    resource="subscription"
                                    disableSyncWithLocation
                                    storeKey="school.pupil.subscription.listParams"
                                    customFilters={['subscriber_code', 'pupil']}
                                    showExtra={['search']}
                                    hideFields={['orderlink']}
                                    sort={{ field: 'product', order: 'ASC' }}
                                    bulkActionButtons={false}
                                    filterDefaultValues={{ isvalid: 'current' }}
                                >
                                    <span />
                                </CustomSubscriptionList>
                            </Grid>
                        </Grid>

                        <Grid container width={{ xs: '100%' }} spacing={2}>
                            <Grid size={12}>
                                <Typography variant="h5" paragraph>
                                    Koululisenssit
                                </Typography>
                                <CustomSubscriptionList
                                    resource="subscription"
                                    disableSyncWithLocation
                                    storeKey="school.teacher.subscription.listParams"
                                    customFilters={[
                                        'subscriber_code',
                                        'teacher',
                                    ]}
                                    showExtra={['search']}
                                    hideFields={[
                                        'used_license_count',
                                        'unused_license_count',
                                        'orderlink',
                                    ]}
                                    sort={{ field: 'product', order: 'ASC' }}
                                    bulkActionButtons={false}
                                    filterDefaultValues={{ isvalid: 'current' }}
                                >
                                    <span />
                                </CustomSubscriptionList>
                            </Grid>
                        </Grid>
                    </FormTab>
                }
                {/* 
                    - Tähän ryhmät.
                    - Sivutettu sortattava listaus.
                    - Haku???
                */}
                <FormTab
                    label="resources.school.tabs.groups"
                    icon={<Diversity1 />}
                    path="ryhmat"
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <CustomGroupList
                                disableSyncWithLocation
                                storeKey="schoolGroupList"
                                resource="group"
                                filter={{}}
                                customFilters={['parent_guid']}
                                showExtra={['search', 'onlyactive']}
                                hideFields={['']}
                                sort={{ field: 'name', order: 'ASC' }}
                                bulkActionButtons={false}
                            >
                                <span />
                            </CustomGroupList>
                        </Grid>
                    </Grid>
                </FormTab>
                {/* 
                    - Tähän opettajat.
                    - Sivutettu sortattava listaus.
                    - Haku???
                */}
                <FormTab
                    label="resources.school.tabs.teachers"
                    icon={<School />}
                    path="opettajat"
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <CustomUserList
                                disableSyncWithLocation
                                storeKey="schoolTeacherList"
                                resource="teacher"
                                filter={{
                                    roletype: '0,1',
                                    status: '0,2,3',
                                    usergroup_status: '0,2,3',
                                }}
                                customFilters={['group']}
                                showExtra={['search']}
                                hideFields={[
                                    'email',
                                    'code',
                                    'middlename',
                                    'loginstudent',
                                ]}
                                sort={{
                                    field: 'public.lastname',
                                    order: 'ASC',
                                }}
                                bulkActionButtons={false}
                                emptyIcon={user.iconTeacher}
                            >
                                <span />
                            </CustomUserList>
                        </Grid>
                    </Grid>
                    {/* <CustomUserList resource='teacher' disableSyncWithLocation customFilters={['group']} showExtra={['search']} hideFields={[]} sort={{ field: 'public.lastname', order: 'ASC' }} bulkActionButtons={false}><span /></CustomUserList> */}
                </FormTab>

                {/* 
                    - Tähän oppilaat.
                    - Sivutettu sortattava listaus.
                    - Haku???
                */}
                <FormTab
                    label="resources.school.tabs.pupils"
                    icon={<Face5 />}
                    path="oppilaat"
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <CustomUserList
                                disableSyncWithLocation
                                storeKey="schoolPupilList"
                                resource="pupil"
                                filter={{
                                    status: '0',
                                    usergroup_status: '0',
                                    sessions: true,
                                }}
                                customFilters={['parent_group']}
                                showExtra={['search']}
                                hideFields={[
                                    'loginteacher',
                                    'subjects',
                                    'role',
                                    'loginstudent',
                                    'email',
                                    'username',
                                ]}
                                sort={{
                                    field: 'public.lastname',
                                    order: 'ASC',
                                }}
                                bulkActionButtons={false}
                                emptyIcon={user.iconPupil}
                            >
                                <span />
                            </CustomUserList>
                        </Grid>
                    </Grid>
                </FormTab>

                {/* 
                    - Tähän opiskelijat.
                    - Sivutettu sortattava listaus.
                    - Haku???
                */}
                <FormTab
                    label="resources.school.tabs.students"
                    icon={<Face />}
                    path="opiskelijat"
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <Grid size={12}>
                            <CustomUserList
                                disableSyncWithLocation
                                storeKey="schoolStudentList"
                                resource="student"
                                customFilters={['group']}
                                showExtra={['search']}
                                filter={{
                                    status: '0',
                                    usergroup_status: '0',
                                    group_status: '0',
                                }}
                                hideFields={[
                                    'code',
                                    'middlename',
                                    'subjects',
                                    'role',
                                    'loginteacher',
                                ]}
                                sort={{
                                    field: 'public.lastname',
                                    order: 'ASC',
                                }}
                                bulkActionButtons={false}
                                emptyIcon={user.iconStudent}
                            >
                                <span />
                            </CustomUserList>
                        </Grid>
                    </Grid>
                </FormTab>
                <FormTab
                    label="merge.workspace.title"
                    icon={<CallMergeIcon />}
                    path="yhdistaminen"
                >
                    {isMergeAccessPending ? null : canManageMerges ? (
                        <SchoolMergeTab />
                    ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {translate('merge.workspace.coming_soon')}
                        </Alert>
                    )}
                </FormTab>
                <FormTab
                    label="resources.school.tabs.stats"
                    icon={<BarChartIcon />}
                    path="kaytto_historia"
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <StatisticsTile permissions={[]} showMpass={false} />
                    </Grid>
                </FormTab>

                <FormTab
                    label="Organisaatiotiedot"
                    icon={
                        <BusinessIcon
                            sx={{
                                color: organizationActive
                                    ? 'inherit'
                                    : '#d32f2f',
                            }}
                        />
                    }
                    path="organisaatio"
                >
                    <Grid container width={{ xs: '100%' }} spacing={2}>
                        <OrganizationInfoTab
                            onStatusChange={setOrganizationActive}
                        />
                    </Grid>
                </FormTab>
            </TabbedForm>
        </Edit>
    );
};

const redirect: any = () => {
    return window.location.hash.replace('#', '');
};

export default SchoolEdit;
